import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Loader2, StickyNote } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import VoiceRecorder from "@/components/VoiceRecorder";
import SpeakButton from "@/components/SpeakButton";

const SUBJECTS = ["Math", "Reading", "Writing", "Science", "Social studies", "Other"];

function StepsMessage({ text }) {
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const stepPattern = /^(\d+)[.)]\s*(.*)$/;
  const steps = [];
  const intro = [];
  let seenStep = false;
  for (const l of lines) {
    const m = l.match(stepPattern);
    if (m) {
      seenStep = true;
      steps.push({ n: m[1], text: m[2] });
    } else if (!seenStep) {
      intro.push(l);
    } else {
      if (steps.length) steps[steps.length - 1].text += " " + l;
    }
  }
  if (steps.length === 0) {
    return <p className="text-lg leading-relaxed whitespace-pre-wrap">{text}</p>;
  }
  return (
    <div className="space-y-3">
      {intro.length > 0 && <p className="text-lg leading-relaxed">{intro.join(" ")}</p>}
      {steps.map((s) => (
        <div className="tm-numbered-step" key={`step-${s.n}`}>
          <span className="tm-numbered-step-num">{s.n}</span>
          <span className="text-lg leading-relaxed">{s.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function Homework() {
  const { id } = useParams();
  const nav = useNavigate();
  const [student, setStudent] = useState(null);
  const [subject, setSubject] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadStudent = useCallback(async () => {
    try {
      const r = await api.get(`/students/${id}`);
      setStudent(r.data);
    } catch {
      nav("/dashboard");
    }
  }, [id, nav]);

  const [pendingNoteCount, setPendingNoteCount] = useState(0);
  const loadNotes = useCallback(async () => {
    try {
      const { data } = await api.get(`/students/${id}/notes`);
      setPendingNoteCount(data.filter((n) => !n.consumed_at).length);
    } catch {
      setPendingNoteCount(0);
    }
  }, [id]);

  useEffect(() => {
    loadStudent();
    loadNotes();
  }, [loadStudent, loadNotes]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || busy) return;
    setInput("");
    const now = new Date().toISOString();
    setMessages((cur) => [...cur, { role: "user", text, created_at: now }]);
    setBusy(true);
    try {
      const { data } = await api.post("/homework/chat", {
        student_id: id,
        session_id: sessionId,
        subject: subject || null,
        message: text,
      });
      setSessionId(data.session_id);
      setMessages((cur) => [
        ...cur,
        { role: "assistant", text: data.reply, created_at: new Date().toISOString() },
      ]);
      // Notes get consumed by this call — refresh the banner count.
      loadNotes();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not get a reply.");
    } finally {
      setBusy(false);
    }
  };

  if (!student) return null;

  return (
    <main className="min-h-screen flex flex-col" style={{ background: "var(--tm-bg-2)" }} data-testid="homework-page">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 py-6 flex-1 flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <Link to={`/students/${id}/home`} className="tm-btn tm-btn-ghost" data-testid="homework-back">
            <ArrowLeft size={18} /> Back
          </Link>
          <h1 className="text-2xl sm:text-3xl font-black flex-1" style={{ fontFamily: "Nunito, sans-serif" }}>
            Homework helper
          </h1>
        </div>

        {pendingNoteCount > 0 && (
          <div className="tm-card-soft mb-4 flex items-center gap-3" data-testid="homework-note-banner">
            <div
              className="p-2 rounded-lg flex-shrink-0"
              style={{ background: "#ffffff", color: "var(--tm-brand)", border: "2px solid var(--tm-border)" }}
            >
              <StickyNote size={18} />
            </div>
            <p className="text-[color:var(--tm-ink-2)]">
              A grown-up left {pendingNoteCount === 1 ? "a note" : `${pendingNoteCount} notes`} to help me answer.
            </p>
          </div>
        )}

        {/* Subject selector */}
        <div className="tm-card mb-4">
          <span className="tm-label">What subject is this for?</span>
          <div className="flex flex-wrap gap-2">
            {SUBJECTS.map((s) => (
              <button
                key={s}
                onClick={() => setSubject(s)}
                className={`tm-btn ${subject === s ? "tm-btn-primary" : "tm-btn-outline"}`}
                style={{ minHeight: 40, padding: "0.4rem 1rem" }}
                data-testid={`homework-subject-${s.toLowerCase().replace(/\s/g, "-")}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Chat area */}
        <div
          ref={scrollRef}
          className="flex-1 tm-card overflow-y-auto mb-4"
          style={{ minHeight: 360, maxHeight: "58vh" }}
          data-testid="homework-messages"
        >
          {messages.length === 0 && (
            <div className="text-center py-10">
              <p className="text-lg text-[color:var(--tm-ink-2)] mb-2">
                Type or speak your homework question below.
              </p>
              <p className="text-[color:var(--tm-ink-3)]">
                Example: "I need help with 24 + 17."
              </p>
            </div>
          )}
          <div className="space-y-5">
            {messages.map((m, i) => (
              <div key={`msg-${m.created_at}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[92%] ${m.role === "user" ? "" : "w-full"}`}
                  data-testid={`msg-${m.role}-${i}`}
                >
                  {m.role === "user" ? (
                    <div
                      className="rounded-2xl px-4 py-3 text-lg"
                      style={{ background: "var(--tm-brand)", color: "#fff" }}
                    >
                      {m.text}
                    </div>
                  ) : (
                    <div className="tm-card-soft">
                      <StepsMessage text={m.text} />
                      <div className="mt-3 flex justify-end">
                        <SpeakButton text={m.text} testId={`msg-speak-${i}`} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="tm-card-soft flex items-center gap-2 text-[color:var(--tm-ink-3)]">
                  <Loader2 size={16} className="animate-spin" /> Thinking about your question…
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input row */}
        <div className="tm-card flex flex-col gap-3">
          <textarea
            className="tm-input resize-none"
            rows={2}
            placeholder="Type your question, or press Speak."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            data-testid="homework-input"
          />
          <div className="flex items-center justify-between gap-2">
            <VoiceRecorder onTranscript={(t) => setInput((v) => (v ? `${v} ${t}` : t))} testIdPrefix="homework" />
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="tm-btn tm-btn-primary"
              data-testid="homework-send-btn"
            >
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              Send
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
